import { z } from 'zod';
import { BIOME_IDS } from './biomes.js';
import { MobIdSchema } from './mobs.js';
import { RARE_MOB_IDS } from './rare-mobs.js';
import { getShopTitle } from './shop-titles.js';
/**
 * Achievements and titles (ADR 0046). Data, not code: what each asks and
 * what it gives. Whether one is done is derived from what the server
 * already keeps (the career, the portals used, the hidden chests opened) by
 * `rules/achievements.ts`, so nothing records an unlock and the old
 * characters get theirs on their next login.
 *
 * Each biome has its own list and a last one, `complete`, done when all the
 * others of its biome are: it gives the biome's title. The feats belong to
 * no biome; a few give a title too. `complete-all` asks every biome's
 * `complete` and gives the one cosmetic no shop sells.
 */
export const CAREER_GOAL_STATS = [
    'kills',
    'deaths',
    'tilesWalked',
    'playSeconds',
    'spellsCast',
    'itemsLooted',
];
const Count = z.number().int().min(1);
const Biome = z.enum(BIOME_IDS);
export const AchievementGoalSchema = z.discriminatedUnion('kind', [
    /** Kills of these mobs, summed: one creature, or a family. */
    z.object({ kind: z.literal('kills'), mobs: z.array(MobIdSchema).min(1), count: Count }),
    /** The village's portal used once (ADR 0017). */
    z.object({ kind: z.literal('portal'), portal: z.string().min(1) }),
    /** Any hidden chest of the biome opened once (ADR 0021). */
    z.object({ kind: z.literal('hidden-chest'), biome: Biome }),
    /** Camp chests of the biome opened after their clear. */
    z.object({ kind: z.literal('camp'), biome: Biome, count: Count }),
    z.object({ kind: z.literal('career'), stat: z.enum(CAREER_GOAL_STATS), count: Count }),
    /** Pages of the hunting log at `tier` or beyond (ADR 0093). */
    z.object({
        kind: z.literal('bestiary'),
        tier: z.number().int().min(1).max(3),
        pages: Count,
    }),
    z.object({ kind: z.literal('complete'), biome: Biome }),
    z.object({ kind: z.literal('complete-all') }),
    /** The account was opened during the beta (ADR 0083): shown only to those who have it. */
    z.object({ kind: z.literal('beta') }),
]);
export const AchievementSchema = z.object({
    id: z.string().regex(/^[a-z][a-z0-9-]{0,31}$/),
    /** French, shown in the window and in the unlock notice. */
    name: z.string().min(1).max(40),
    description: z.string().min(1).max(80),
    /** The biome whose list it is in; none for a feat. */
    biome: Biome.optional(),
    goal: AchievementGoalSchema,
    /** The title it unlocks, French, worn above the name. */
    title: z.string().min(1).max(28).optional(),
    /** A template given once, claimed from the window (the bag may be full). */
    reward: z.string().min(1).optional(),
});
function hunt(biome, mob, count, name, prey) {
    return {
        id: `${biome}-${mob}`.slice(0, 32),
        biome,
        name,
        description: `Vaincre ${String(count)} ${prey}`,
        goal: { kind: 'kills', mobs: [mob], count },
    };
}
function slay(biome, mob, name, description) {
    return {
        id: `${biome}-${mob}`.slice(0, 32),
        biome,
        name,
        description,
        goal: { kind: 'kills', mobs: [mob], count: 1 },
    };
}
/** What every biome asks besides its creatures: its portal, a hidden chest, a camp. */
function explore(biome, village) {
    return [
        {
            id: `${biome}-portal`,
            biome,
            name: 'Pierre de voyage',
            description: `Activer le portail de ${village}`,
            goal: { kind: 'portal', portal: biome },
        },
        {
            id: `${biome}-hidden-chest`,
            biome,
            name: 'Fouineur',
            description: 'Ouvrir un coffre caché de la région',
            goal: { kind: 'hidden-chest', biome },
        },
        {
            id: `${biome}-camp`,
            biome,
            name: 'Pillard de camp',
            description: 'Nettoyer un camp de monstres et ouvrir son coffre',
            goal: { kind: 'camp', biome, count: 1 },
        },
    ];
}
function complete(biome, name, title) {
    return {
        id: `${biome}-complete`,
        biome,
        name,
        description: 'Accomplir tous les succès de la région',
        goal: { kind: 'complete', biome },
        title,
    };
}
const DEFINITIONS = [
    // Prairie, levels 1-4: the counts grow with the biome, as its packs do.
    hunt('meadow', 'rat', 25, 'Chasse aux nuisibles', 'rats'),
    hunt('meadow', 'fox', 25, 'Rusé comme un renard', 'renards'),
    hunt('meadow', 'boar', 25, 'Groin pour groin', 'sangliers'),
    slay('meadow', 'plague-rat', 'La peste soit du rat', 'Vaincre le Rat pesteux'),
    slay('meadow', 'scarred-boar', 'Cicatrices', 'Vaincre le Sanglier balafré'),
    slay('meadow', 'iron-snout', 'Nez de fer', 'Vaincre Groin-de-Fer'),
    slay('meadow', 'great-scarecrow', 'Moisson maudite', 'Vaincre le Grand Épouvantail'),
    ...explore('meadow', 'Havrebourg'),
    complete('meadow', 'Maître de la Prairie', 'Gardien de la Prairie'),
    // Forêt des Murmures, levels 5-7.
    hunt('forest', 'wolf', 30, 'La meute', 'loups'),
    hunt('forest', 'treant', 30, 'Bûcheron', 'sylvains'),
    hunt('forest', 'goblin', 30, 'Chasseur de gobelins', 'gobelins'),
    hunt('forest', 'goblin-archer', 30, 'Tir de barrage', 'archers gobelins'),
    slay('forest', 'ash-wolf', 'Cendres', 'Vaincre le Loup cendré'),
    slay('forest', 'goblin-chief', 'Décapiter la horde', 'Vaincre le Chef gobelin'),
    slay('forest', 'silver-fang', 'Croc brisé', 'Vaincre Croc-d’Argent'),
    slay('forest', 'werewolf', 'Nuit de pleine lune', 'Vaincre le Loup-Garou'),
    ...explore('forest', 'Sylvebois'),
    complete('forest', 'Maître de la Forêt', 'Veilleur des Murmures'),
    // Marais Brumeux, levels 8-11.
    hunt('swamp', 'toad', 35, 'Coassements', 'crapauds géants'),
    hunt('swamp', 'caiman', 35, 'Maroquinier', 'caïmans'),
    hunt('swamp', 'lizardman', 35, 'Écailles', 'hommes-lézards'),
    hunt('swamp', 'wisp', 35, 'Éteindre les lueurs', 'feux follets'),
    slay('swamp', 'venom-toad', 'Antidote', 'Vaincre le Crapaud venimeux'),
    slay('swamp', 'lizard-shaman', 'Silence, sorcier', 'Vaincre le Chaman lézard'),
    slay('swamp', 'bog-king', 'Régicide', 'Vaincre le Roi des Tourbières'),
    slay('swamp', 'bog-troll', 'Sous le pont', 'Vaincre le Troll des Tourbières'),
    ...explore('swamp', 'Brumevase'),
    complete('swamp', 'Maître du Marais', 'Seigneur des Brumes'),
    // Désert de Braise, levels 12-15.
    hunt('desert', 'hyena', 40, 'Rires étouffés', 'hyènes'),
    hunt('desert', 'scorpion', 40, 'Dard pour dard', 'scorpions géants'),
    hunt('desert', 'ember-salamander', 40, 'Pompier', 'salamandres de braise'),
    hunt('desert', 'mummy', 40, 'Bandelettes', 'momies'),
    hunt('desert', 'cobra', 40, 'Charmeur', 'cobras cracheurs'),
    slay('desert', 'mangy-hyena', 'Galeux', 'Vaincre la Hyène galeuse'),
    slay('desert', 'cursed-mummy', 'Brise-malédiction', 'Vaincre la Momie maudite'),
    slay('desert', 'forgotten-pharaoh', 'Mémoire', 'Vaincre le Pharaon oublié'),
    slay('desert', 'golden-jackal', 'Or terni', 'Vaincre le Chacal d’Or'),
    ...explore('desert', 'Sablerouge'),
    complete('desert', 'Maître du Désert', 'Enfant de la Braise'),
    // Pics Gelés, levels 16-20.
    hunt('snow', 'ice-wolf', 50, 'Hurlements', 'loups des glaces'),
    hunt('snow', 'polar-bear', 50, 'Fourreur', 'ours blancs'),
    hunt('snow', 'frost-revenant', 50, 'Dégel', 'revenants du givre'),
    hunt('snow', 'yeti', 50, 'Il existe', 'yétis'),
    hunt('snow', 'snow-harpy', 50, 'Plumée', 'harpies des neiges'),
    slay('snow', 'ice-bear', 'Hibernation', 'Vaincre l’Ours des glaces'),
    slay('snow', 'howling-yeti', 'Silence', 'Vaincre le Yéti hurleur'),
    slay('snow', 'elder-blizzard', 'Fin de la tempête', 'Vaincre le Vieux Blizzard'),
    slay('snow', 'frost-giant', 'Le trône est vide', 'Vaincre le Géant de Givre'),
    ...explore('snow', 'Givrecime'),
    complete('snow', 'Maître des Pics', 'Vainqueur des Pics'),
    // Feats: no biome, long hauls, a title for the proudest.
    {
        id: 'feat-rats',
        name: 'Dératisation',
        description: 'Vaincre 500 rats',
        goal: { kind: 'kills', mobs: ['rat', 'plague-rat'], count: 500 },
        title: 'Dératiseur',
    },
    {
        id: 'feat-wolves',
        name: 'Fléau des loups',
        description: 'Vaincre 300 loups de toute espèce',
        goal: { kind: 'kills', mobs: ['wolf', 'ash-wolf', 'silver-fang', 'ice-wolf'], count: 300 },
        title: 'Fléau des Loups',
    },
    {
        id: 'feat-elites',
        name: 'Chasse aux trophées',
        description: 'Vaincre 30 élites',
        goal: {
            kind: 'kills',
            mobs: [
                'plague-rat',
                'scarred-boar',
                'iron-snout',
                'ash-wolf',
                'goblin-chief',
                'silver-fang',
                'venom-toad',
                'lizard-shaman',
                'bog-king',
                'mangy-hyena',
                'cursed-mummy',
                'forgotten-pharaoh',
                'ice-bear',
                'howling-yeti',
                'elder-blizzard',
            ],
            count: 30,
        },
        title: 'Traqueur d’Élites',
    },
    {
        id: 'feat-bosses',
        name: 'Tueur de titans',
        description: 'Vaincre 25 boss de région',
        goal: {
            kind: 'kills',
            mobs: ['great-scarecrow', 'werewolf', 'bog-troll', 'golden-jackal', 'frost-giant'],
            count: 25,
        },
        title: 'Tueur de Titans',
    },
    {
        // The wandering rares (ADR 0094): found by chance, so five of any.
        id: 'feat-rares',
        name: 'Chasseur de rares',
        description: 'Vaincre 5 monstres rares',
        goal: { kind: 'kills', mobs: [...RARE_MOB_IDS], count: 5 },
        title: 'Chasseur de Rares',
    },
    {
        // The Company's vow kept (ADR 0068 §4): Shefira slain in a raid. Her kill
        // is only ever counted there (`shareShefiraKill` records it with
        // `shefiraKills`); her flight at the end of the story counts nothing.
        id: 'feat-shefira',
        name: 'Le serment tenu',
        description: 'Abattre Shefira en raid',
        goal: { kind: 'kills', mobs: ['shefira'], count: 1 },
        title: 'Héritier de Givrecoeur',
    },
    // The hunting log's three tiers (ADR 0093), each with its title.
    {
        id: 'feat-log-tracker',
        name: 'Carnet bien tenu',
        description: 'Mener 10 pages du carnet de chasse au palier 10',
        goal: { kind: 'bestiary', tier: 1, pages: 10 },
        title: 'Pisteur',
    },
    {
        id: 'feat-log-hunter',
        name: 'Chasseur aguerri',
        description: 'Mener 5 pages du carnet de chasse au palier 100',
        goal: { kind: 'bestiary', tier: 2, pages: 5 },
        title: 'Traqueur',
    },
    {
        id: 'feat-log-scourge',
        name: 'Fléau des bêtes',
        description: 'Mener une page du carnet de chasse au palier 1 000',
        goal: { kind: 'bestiary', tier: 3, pages: 1 },
        title: 'Fléau des Bêtes',
    },
    {
        id: 'feat-kills',
        name: 'Massacre',
        description: 'Vaincre 5 000 ennemis',
        goal: { kind: 'career', stat: 'kills', count: 5000 },
        title: 'Massacreur',
    },
    {
        id: 'feat-deaths',
        name: 'Increvable',
        description: 'Tomber 50 fois',
        goal: { kind: 'career', stat: 'deaths', count: 50 },
        title: 'Habitué du Cimetière',
    },
    {
        id: 'feat-walker',
        name: 'Semelles usées',
        description: 'Parcourir 50 000 cases',
        goal: { kind: 'career', stat: 'tilesWalked', count: 50000 },
        title: 'Grand Voyageur',
    },
    {
        id: 'feat-looter',
        name: 'Poches pleines',
        description: 'Ramasser 1 000 objets',
        goal: { kind: 'career', stat: 'itemsLooted', count: 1000 },
    },
    {
        id: 'feat-spells',
        name: 'Cri de guerre',
        description: 'Lancer 1 000 sorts',
        goal: { kind: 'career', stat: 'spellsCast', count: 1000 },
    },
    {
        // The thanks to the beta testers (ADR 0083): never sold, never earned after the beta.
        id: 'beta-tester',
        name: 'Bêta-testeur',
        description: 'Avoir joué à Shefira pendant sa bêta',
        goal: { kind: 'beta' },
        title: 'Bêta-testeur',
        reward: 'glitch',
    },
    {
        id: 'conqueror',
        name: 'Les Cinq Terres',
        description: 'Accomplir tous les succès des cinq régions',
        goal: { kind: 'complete-all' },
        title: 'Conquérant des Cinq Terres',
        reward: 'conqueror-blade',
    },
];
export const ACHIEVEMENTS = Object.freeze(DEFINITIONS.map((raw) => AchievementSchema.parse(raw)));
const BY_ID = new Map(ACHIEVEMENTS.map((a) => [a.id, a]));
export function getAchievement(id) {
    return BY_ID.get(id);
}
/** The title an id names: an achievement's, or a shop offer's (`shop-titles.ts`). */
export function titleOf(titleId) {
    return BY_ID.get(titleId)?.title ?? getShopTitle(titleId)?.title;
}
/** A biome's list, `complete` last, in the order of the data. */
export function achievementsOf(biome) {
    return ACHIEVEMENTS.filter((achievement) => achievement.biome === biome);
}
//# sourceMappingURL=achievements.js.map